import React, { useState, useEffect } from "react";
import axios from "axios";

const extractValidUrl = (url) => {
    // Match the URL inside the square brackets or parentheses
    const matches = url.match(/\((https?:\/\/[^\)]+)\)/) || url.match(/\[(https?:\/\/[^\]]+)\]/);
    return matches ? matches[1] : url; // Return the matched URL or the original if no match
};

// Function to fetch related articles using ChatGPT
const fetchRelatedArticles = async (backEndUrl, query) => {
    try {
        // Replace with your actual API Gateway endpoint
        const apiGatewayEndpoint = `${backEndUrl}/related-articles`;

        // Make the request to your backend Lambda function
        const response = await axios.get(apiGatewayEndpoint, {
            params: { query }, // Pass the query as a parameter
            headers: {
                "Content-Type": "application/json",
            },
        });

        // Extract articles from the Lambda function's response
        const { articles } = response.data;

        // Return the articles
        return articles;
    } catch (error) {
        console.error("Error fetching related articles from Lambda:", error);
        return [];
    }
};

const GradeRow = ({ grade, gradeColumn, instructions, articles }) => (
    <tr>
        <td>{grade !== null ? grade : "No Grade Available"}</td>
        <td>{gradeColumn?.name || "Unknown Column"}</td>
        <td dangerouslySetInnerHTML={{ __html: instructions || "No Instructions Available" }} />
        <td>
            {articles.length > 0 ? (
                <ul>
                    {articles.map((article, index) => (
                        <li key={index}>
                            <a href={article.url} target="_blank" rel="noopener noreferrer">
                                {article.title}
                            </a>
                        </li>
                    ))}
                </ul>
            ) : (
                "No related articles found."
            )}
        </td>
    </tr>
);

const CourseTutorTool = ({ platformUrl, backEndUrl }) => {
    const [gradeColumns, setGradeColumns] = useState([]);
    const [instructionsMap, setInstructionsMap] = useState({});
    const [gradesMap, setGradesMap] = useState({});
    const [articlesMap, setArticlesMap] = useState({});
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get("token");
        const uuid = params.get("uuid");
        const courseUuid = params.get("courseUuid");

        if (!token || !uuid || !courseUuid) {
            setError("Missing access token, UUID, or course UUID. Please log in.");
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            try {
                // Step 1: Get the course ID using the course UUID
                const courseResponse = await axios.get(
                    `${platformUrl}/learn/api/public/v1/courses/uuid:${courseUuid}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                const courseId = courseResponse.data.id;

                // Step 2: Fetch grade columns
                const gradeColumnsResponse = await axios.get(
                    `${platformUrl}/learn/api/public/v1/courses/${courseId}/gradebook/columns`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                const gradeColumnsData = gradeColumnsResponse.data.results.filter(
                    (column) => column.name !== "Overall Grade"
                );
                setGradeColumns(gradeColumnsData);

                // Step 3: Fetch grades and content details
                const gradePromises = gradeColumnsData.map(async (column) => {
                    const gradeResponse = await axios.get(
                        `${platformUrl}/learn/api/public/v1/courses/${courseId}/gradebook/columns/${column.id}/users/uuid:${uuid}`,
                        {
                            headers: { Authorization: `Bearer ${token}` },
                        }
                    );

                    const gradeData = gradeResponse.data.score || null;

                    // Fetch content for each grade column using its contentId
                    let instructions = "No Instructions Available";
                    if (column.contentId) {
                        const contentResponse = await axios.get(
                            `${platformUrl}/learn/api/v1/courses/${courseId}/contents/${column.contentId}`,
                            {
                                headers: { Authorization: `Bearer ${token}` },
                            }
                        );

                        const contentData = contentResponse.data;

                        // Extract instructions from the contentDetail structure
                        instructions =
                            contentData.contentDetail?.["resource/x-bb-asmt-test-link"]?.test?.assessment?.instructions?.rawText ||
                            "No Instructions Available";
                    }

                    // Fetch related articles using ChatGPT
                    const articles = await fetchRelatedArticles(backEndUrl, instructions);

                    return { columnId: column.id, grade: gradeData, instructions, articles };
                });

                const gradesData = await Promise.all(gradePromises);
                const gradesMap = gradesData.reduce((acc, item) => {
                    acc[item.columnId] = { grade: item.grade, instructions: item.instructions, articles: item.articles };
                    return acc;
                }, {});

                setInstructionsMap(
                    Object.fromEntries(Object.entries(gradesMap).map(([key, value]) => [key, value.instructions]))
                );
                setGradesMap(
                    Object.fromEntries(Object.entries(gradesMap).map(([key, value]) => [key, value.grade]))
                );
                setArticlesMap(
                    Object.fromEntries(Object.entries(gradesMap).map(([key, value]) => [key, value.articles]))
                );
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const highScores = gradeColumns.filter((column) => gradesMap[column.id] > 90);
    const lowScores = gradeColumns.filter((column) => gradesMap[column.id] < 75);

    if (loading) {
        return <div className="bb-loading">Loading...</div>;
    }

    if (error) {
        return (
            <div className="bb-error">
                <h1>Error</h1>
                <p>{error}</p>
            </div>
        );
    }

    const renderTable = (columns, title) => (
        <div>
            <h2>{title}</h2>
            {columns.length === 0 ? (
                <p>No items to display.</p>
            ) : (
                <table className="bb-table" style={{ width: "100%", textAlign: "center" }}>
                    <thead>
                    <tr>
                        <th style={{ textAlign: "center" }}>Grade</th>
                        <th style={{ textAlign: "center" }}>Grade Column</th>
                        <th style={{ textAlign: "center" }}>Instructions</th>
                        <th style={{ textAlign: "center" }}>To Improve Your Understanding</th>
                    </tr>
                    </thead>
                    <tbody>
                    {columns.map((column) => (
                        <GradeRow
                            key={column.id}
                            gradeColumn={column}
                            grade={gradesMap[column.id]}
                            instructions={instructionsMap[column.id]}
                            articles={articlesMap[column.id] || []}
                        />
                    ))}
                    </tbody>
                </table>
            )}
        </div>
    );

    return (
        <div className="course-tutor-tool-container">
            <h1>Course Tutor Tool</h1>
            {renderTable(highScores, "High Scores (Grade > 90)")}
            {renderTable(lowScores, "Low Scores (Grade < 75)")}
        </div>
    );
};

export default CourseTutorTool;
